// Revenue tab with breakdowns by city, medication, brand, source, landing page, sales rep.
import { useCallback, useEffect, useState } from "react";
import { DollarSign, MapPin, Pill, BarChart3, Zap, Compass, Layout, Users } from "lucide-react";
import { API, RED, NEON, AMBER, PURPLE, fmt$, ago, authHeader } from "./shared";

const BRAND_COLOR = {
  BestRx:  RED,
  LeenRx:  PURPLE,
  HappiRx: NEON,
};

export function RevenueBreakdown({ token, payments, onSeeded, cityFilter }) {
  const [byCity, setByCity] = useState([]);
  const [byMed,  setByMed]  = useState([]);
  const [byBrand,setByBrand]= useState([]);
  const [bySource, setBySource] = useState([]);
  const [byLanding, setByLanding] = useState([]);
  const [byRep, setByRep] = useState([]);
  const [seeding, setSeeding] = useState(false);

  const refresh = useCallback(() => {
    const q = cityFilter ? `?city=${encodeURIComponent(cityFilter)}` : "";
    Promise.all([
      fetch(`${API}/reports/revenue-by-city${q}`,         { headers: authHeader(token) }).then((r) => r.ok ? r.json() : []),
      fetch(`${API}/reports/revenue-by-medication${q}`,   { headers: authHeader(token) }).then((r) => r.ok ? r.json() : []),
      fetch(`${API}/reports/revenue-by-brand${q}`,        { headers: authHeader(token) }).then((r) => r.ok ? r.json() : []),
      fetch(`${API}/reports/revenue-by-source${q}`,       { headers: authHeader(token) }).then((r) => r.ok ? r.json() : []),
      fetch(`${API}/reports/revenue-by-landing-page${q}`, { headers: authHeader(token) }).then((r) => r.ok ? r.json() : []),
      fetch(`${API}/reports/revenue-by-sales-rep${q}`,    { headers: authHeader(token) }).then((r) => r.ok ? r.json() : []),
    ]).then(([c, m, b, s, lp, rep]) => {
      setByCity(c || []); setByMed(m || []); setByBrand(b || []);
      setBySource(s || []); setByLanding(lp || []); setByRep(rep || []);
    });
  }, [token, cityFilter]);

  useEffect(() => { refresh(); const id = setInterval(refresh, 8000); return () => clearInterval(id); }, [refresh]);

  // Total: prefer backend breakdown (already filtered) over raw payments prop.
  const total = (byCity || []).reduce((s, r) => s + (r.revenue_cents || 0), 0)
    || payments.reduce((s, p) => s + (p.amount_cents || 0), 0);

  async function seed() {
    setSeeding(true);
    await fetch(`${API}/demo/seed-revenue`, { method: "POST", headers: authHeader(token) });
    setSeeding(false);
    refresh();
    onSeeded && onSeeded();
  }

  return (
    <div className="space-y-4" data-testid="revenue">
      {/* Headline */}
      <div className="rounded-sm border border-white/10 bg-black/50">
        <div className="px-5 py-3 border-b border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="font-heavy text-[14px] inline-flex items-center gap-2 flex-wrap">
            <DollarSign size={14} color={NEON} /> REVENUE — TOTAL {fmt$(total)}
            {cityFilter && (
              <span className="px-1.5 py-0.5 rounded-sm border text-[9.5px] font-extrabold tracking-[0.12em]"
                style={{ borderColor: RED, color: RED, background: "rgba(255,42,60,0.10)" }}
                data-testid="revenue-city-filter">
                CITY: {cityFilter.toUpperCase()}
              </span>
            )}
          </p>
          <button
            onClick={seed}
            disabled={seeding}
            data-testid="revenue-seed"
            className="self-start sm:self-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-[10.5px] font-extrabold tracking-[0.12em] uppercase border"
            style={{ borderColor: AMBER, color: AMBER }}
            title="Backfill payments + assessments + follow-ups for existing leads (demo only)"
          >
            <Zap size={11} /> {seeding ? "Seeding…" : "Seed Demo Revenue"}
          </button>
        </div>
      </div>

      {/* Standard trio (city / med / brand) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <BreakdownCard title="By City"       Icon={MapPin}    rows={byCity}  testid="rev-by-city" />
        <BreakdownCard title="By Medication" Icon={Pill}      rows={byMed}   testid="rev-by-med" />
        <BreakdownCard title="By Brand"      Icon={BarChart3} rows={byBrand} testid="rev-by-brand"
          colorFor={(r) => BRAND_COLOR[r.label] || NEON} />
      </div>

      {/* Attribution trio (source / landing / rep) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <BreakdownCard title="By Source"        Icon={Compass} rows={bySource}  testid="rev-by-source"  showLeads />
        <BreakdownCard title="By Landing Page"  Icon={Layout}  rows={byLanding} testid="rev-by-landing" showLeads />
        <BreakdownCard title="By Sales Rep"     Icon={Users}   rows={byRep}     testid="rev-by-rep"     showLeads />
      </div>

      {/* Raw payment stream */}
      <div className="rounded-sm border border-white/10 bg-black/50">
        <div className="px-5 py-3 border-b border-white/10">
          <p className="font-heavy text-[14px] inline-flex items-center gap-2">
            <DollarSign size={13} color={NEON} /> RAW PAYMENT STREAM
          </p>
        </div>
        <div className="divide-y divide-white/5 max-h-[360px] overflow-y-auto">
          {payments.length === 0 && (
            <p className="px-5 py-10 text-center text-white/45 text-[13px]">
              No payments yet. Click <span style={{ color: AMBER }} className="font-bold">Seed Demo Revenue</span> to backfill.
            </p>
          )}
          {payments.map((p) => (
            <div key={p.id} className="grid grid-cols-12 gap-3 px-5 py-2.5" data-testid={`payment-${p.id.slice(0,8)}`}>
              <div className="col-span-3 font-heavy text-[13px]" style={{ color: NEON }}>{fmt$(p.amount_cents)}</div>
              <div className="col-span-5 text-[11.5px] text-white/70 font-mono truncate">{p.email}</div>
              <div className="col-span-2 text-[11px] text-white/55">{p.processor}</div>
              <div className="col-span-2 text-right text-[10.5px] text-white/45 font-mono">{ago(p.created_at)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BreakdownCard({ title, Icon, rows, testid, colorFor, showLeads }) {
  const max = Math.max(1, ...rows.map((r) => r.revenue_cents || 0));
  return (
    <div className="rounded-sm border border-white/10 bg-black/50" data-testid={testid}>
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <p className="font-heavy text-[12.5px] inline-flex items-center gap-2">
          <Icon size={12} color={RED} /> {title.toUpperCase()}
        </p>
        <span className="text-[10px] tracking-[0.16em] font-bold text-white/45">{rows.length}</span>
      </div>
      <div className="p-4 space-y-2 max-h-[320px] overflow-y-auto">
        {rows.length === 0 && <p className="text-white/45 text-[12px] text-center py-6">No data yet.</p>}
        {rows.map((r) => {
          const pct = (r.revenue_cents / max) * 100;
          const c = (colorFor && colorFor(r)) || NEON;
          return (
            <div key={r.label} className="space-y-1" data-testid={`${testid}-row-${(r.label || "_").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 24)}`}>
              <div className="flex items-center justify-between text-[11.5px]">
                <span className="font-extrabold text-white tracking-tight truncate pr-3">{r.label || "—"}</span>
                <span className="font-heavy" style={{ color: c }}>{fmt$(r.revenue_cents)}</span>
              </div>
              <div className="h-1.5 bg-white/5 rounded-sm overflow-hidden">
                <div className="h-full rounded-sm transition-all duration-700"
                  style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${c} 0%, ${c}AA 100%)` }} />
              </div>
              <p className="text-[9.5px] tracking-[0.14em] font-bold text-white/40">
                {r.payments} payments{showLeads && r.leads !== undefined ? ` · ${r.leads} leads` : ""}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
